import { supabase } from '@/integrations/supabase/client';
import {
  fetchChecklistFieldMappingsForItems,
  fetchChecklistItemFieldMappings,
  replaceChecklistItemFieldMappings,
} from './field-lookups';
import type {
  ChecklistFieldMapping,
  ChecklistItem,
  CreateChecklistItemInput,
  UpdateChecklistItemInput,
} from './types';

type ChecklistItemRow = Omit<ChecklistItem, 'checklist_id' | 'field_mappings' | 'field_mapping_id'> & {
  template_id: string;
};

function normalizeFieldMappingIds(
  fieldMappingId?: string | null,
  fieldMappingIds?: string[],
): string[] {
  if (fieldMappingIds && fieldMappingIds.length > 0) {
    return [...new Set(fieldMappingIds.filter(Boolean))];
  }
  if (fieldMappingId) return [fieldMappingId];
  return [];
}

function mapChecklistItemRow(
  row: ChecklistItemRow,
  fieldMappings: ChecklistFieldMapping[] = [],
): ChecklistItem {
  return {
    id: row.id,
    tenant_id: row.tenant_id,
    checklist_id: row.template_id,
    label: row.label,
    description: row.description,
    item_type: row.item_type,
    is_required: row.is_required,
    is_inspection: row.is_inspection,
    dropdown_options: row.dropdown_options,
    sort_order: row.sort_order,
    area_id: row.area_id,
    line_id: row.line_id,
    station_id: row.station_id,
    label_translations: row.label_translations,
    description_translations: row.description_translations,
    created_at: row.created_at,
    updated_at: row.updated_at,
    field_mapping_id: fieldMappings[0]?.field_mapping_id ?? null,
    field_mappings: fieldMappings,
  };
}

export async function fetchChecklistItems(
  tenantId: string,
  checklistId: string,
): Promise<ChecklistItem[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_template_items')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('template_id', checklistId)
    .order('sort_order');

  if (error) throw error;

  const itemRows = (data ?? []) as ChecklistItemRow[];
  const itemIds = itemRows.map((row) => row.id);
  const mappingByItem = await fetchChecklistFieldMappingsForItems(tenantId, itemIds);

  return itemRows.map((row) => mapChecklistItemRow(row, mappingByItem[row.id] ?? []));
}

export async function createChecklistItem(
  input: CreateChecklistItemInput,
): Promise<ChecklistItem> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_template_items')
    .insert({
      tenant_id: input.tenant_id,
      template_id: input.checklist_id,
      label: input.label,
      description: input.description ?? null,
      item_type: input.item_type ?? 'yes_no',
      is_required: input.is_required ?? true,
      is_inspection: input.is_inspection ?? false,
      dropdown_options: input.dropdown_options ?? null,
      sort_order: input.sort_order ?? 0,
      area_id: input.area_id ?? null,
      line_id: input.line_id ?? null,
      station_id: input.station_id ?? null,
      label_translations: input.label_translations ?? {},
      description_translations: input.description_translations ?? {},
    })
    .select('*')
    .single();

  if (error) throw error;

  const mappingIds = normalizeFieldMappingIds(
    input.field_mapping_id,
    input.field_mapping_ids,
  );
  await replaceChecklistItemFieldMappings(input.tenant_id, data.id, mappingIds);
  const fieldMappings = await fetchChecklistItemFieldMappings(input.tenant_id, data.id);

  return mapChecklistItemRow(data as ChecklistItemRow, fieldMappings);
}

export async function createChecklistItemsBatch(
  items: CreateChecklistItemInput[],
): Promise<ChecklistItem[]> {
  if (items.length === 0) return [];

  const rows = items.map((item) => ({
    tenant_id: item.tenant_id,
    template_id: item.checklist_id,
    label: item.label,
    description: item.description ?? null,
    item_type: item.item_type ?? 'yes_no',
    is_required: item.is_required ?? true,
    is_inspection: item.is_inspection ?? false,
    dropdown_options: item.dropdown_options ?? null,
    sort_order: item.sort_order ?? 0,
    area_id: item.area_id ?? null,
    line_id: item.line_id ?? null,
    station_id: item.station_id ?? null,
    label_translations: item.label_translations ?? {},
    description_translations: item.description_translations ?? {},
  }));

  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_template_items')
    .insert(rows)
    .select('*');

  if (error) throw error;

  const inserted = (data ?? []) as ChecklistItemRow[];
  for (let index = 0; index < inserted.length; index += 1) {
    const row = inserted[index];
    const source = items[index];
    const mappingIds = normalizeFieldMappingIds(
      source?.field_mapping_id,
      source?.field_mapping_ids,
    );
    if (mappingIds.length > 0) {
      await replaceChecklistItemFieldMappings(row.tenant_id, row.id, mappingIds);
    }
  }

  const mappingByItem = await fetchChecklistFieldMappingsForItems(
    inserted[0].tenant_id,
    inserted.map((row) => row.id),
  );

  return inserted.map((row) => mapChecklistItemRow(row, mappingByItem[row.id] ?? []));
}

export async function updateChecklistItem(
  input: UpdateChecklistItemInput,
): Promise<void> {
  const {
    id,
    tenant_id,
    field_mapping_id,
    field_mapping_ids,
    ...updates
  } = input;

  const payload: Record<string, unknown> = {
    label: updates.label,
    description: updates.description,
    item_type: updates.item_type,
    is_required: updates.is_required,
    is_inspection: updates.is_inspection,
    dropdown_options: updates.dropdown_options,
    area_id: updates.area_id,
    line_id: updates.line_id,
    station_id: updates.station_id,
    label_translations: updates.label_translations,
    description_translations: updates.description_translations,
  };

  const sanitizedPayload = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  );

  if (Object.keys(sanitizedPayload).length > 0) {
    const { error } = await supabase
      .schema('core')
      .from('mod_checklist_template_items')
      .update(sanitizedPayload)
      .eq('tenant_id', tenant_id)
      .eq('id', id);

    if (error) throw error;
  }

  if (field_mapping_ids !== undefined || field_mapping_id !== undefined) {
    const mappingIds = normalizeFieldMappingIds(field_mapping_id, field_mapping_ids);
    await replaceChecklistItemFieldMappings(tenant_id, id, mappingIds);
  }
}

export async function updateChecklistItemSortOrder(
  tenantId: string,
  itemId: string,
  sortOrder: number,
): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('mod_checklist_template_items')
    .update({ sort_order: sortOrder })
    .eq('tenant_id', tenantId)
    .eq('id', itemId);

  if (error) throw error;
}

export async function deleteChecklistItem(
  tenantId: string,
  itemId: string,
): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('mod_checklist_template_items')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('id', itemId);

  if (error) throw error;
}
